/**
 * #1098 — wait for renderer list after upgrade before accepting System Audio fallback.
 */

import { useMaestroStore } from '../store/maestroStore';
import { isConnectorAvailable, setVolume as connectorSetVolume } from '../lib/connector';
import { isIOSNativeApp } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { isLocalZoneStorageId, isSystemAudioZone, userExplicitlyChoseSystemAudio } from '../utils/localZoneStorage';
import { canRestoreSavedZone } from './playbackSession';
import {
  applyPinnedSavedZone,
  applyZoneSelectionToStore,
  findPreferredZone,
  getSavedConnectorZoneId,
  isAlreadyOnSavedZone,
} from './zoneRestorePick';
import { hydrateConnectorZoneStorageFromDisk, readSavedZoneVolume } from './connectorLastPlaybackSession';

export const BOOT_ZONE_RESTORE_WAIT_MS = 30_000;
const ZONE_POLL_MS = 250;

function logZoneRestore(outcome: string, detail: Record<string, unknown> = {}): void {
  diagnosticLog('info', 'zone_restore', outcome, detail);
}

async function applySavedZoneVolume(rendererId: string): Promise<void> {
  const vol = readSavedZoneVolume();
  if (vol == null) return;
  try {
    await connectorSetVolume(rendererId, vol);
    useMaestroStore.getState().setVolume(vol);
  } catch { /* device may not be ready */ }
}

async function syncDiscoveryActiveZone(zoneId: string): Promise<void> {
  try {
    const { getClientDiscoveryHandler } = await import('../handlers/ClientDiscoveryHandler');
    await getClientDiscoveryHandler().setActiveZone(zoneId).catch(() => {});
  } catch { /* optional */ }
}

export async function prepareConnectorDiscoveryBoot(): Promise<void> {
  await hydrateConnectorZoneStorageFromDisk();
}

type ZoneLike = { type?: string; id?: string };

export function shouldAllowConnectorZoneRestoreRetry(input: {
  savedId: string | null;
  activeZone: ZoneLike | null | undefined;
  isLocalZone: (zone: ZoneLike) => boolean;
  hasActiveRendererSelection: () => boolean;
}): boolean {
  if (!canRestoreSavedZone()) return false;
  const { savedId, activeZone, isLocalZone, hasActiveRendererSelection } = input;
  if (savedId && !isLocalZoneStorageId(savedId) && activeZone && isLocalZone(activeZone) && !userExplicitlyChoseSystemAudio()) {
    return true;
  }
  return !hasActiveRendererSelection();
}

export function wireConnectorDiscoveryBoot(handler: {
  setActiveZone: (zoneId: string) => Promise<unknown>;
  shouldRetryConnectorZoneRestore: () => boolean;
}): void {
  void prepareConnectorDiscoveryBoot().then((seeded) => {
    if (seeded) console.log('[ClientDiscoveryHandler] Hydrated saved zone from Connector disk session');
    installMaestroZoneRestoreReadyListener((id) => handler.setActiveZone(id), () => handler.shouldRetryConnectorZoneRestore());
  });
}

export function installMaestroZoneRestoreReadyListener(
  setActiveZone: (zoneId: string) => Promise<unknown>,
  shouldRetry: () => boolean,
): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('maestro-zone-restore-ready', ((event: CustomEvent) => {
    const zoneId = event.detail?.zoneId as string | undefined;
    if (!zoneId || !canRestoreSavedZone() || !shouldRetry()) return;
    void setActiveZone(zoneId);
  }) as EventListener);
}

export async function runBootZoneRestoreWatch(): Promise<void> {
  if (!isConnectorAvailable() && !isIOSNativeApp()) return;
  if (!canRestoreSavedZone()) {
    logZoneRestore('skipped_session_active', {});
    return;
  }
  const savedId = getSavedConnectorZoneId();
  if (!savedId) {
    logZoneRestore('skipped_no_saved', {});
    return;
  }
  if (isLocalZoneStorageId(savedId)) {
    logZoneRestore('skipped_local_saved', { savedId });
    return;
  }

  let zones = useMaestroStore.getState().discovery?.discoveredZones || [];
  let savedZone = findPreferredZone(savedId, zones);
  if (savedZone && !isAlreadyOnSavedZone(savedId)) {
    applyZoneSelectionToStore(savedZone);
    await syncDiscoveryActiveZone(savedZone.id);
    await applySavedZoneVolume(savedZone.id);
    logZoneRestore('restored', { zoneId: savedZone.id, zoneName: savedZone.name, phase: 'immediate' });
    return;
  }
  if (isAlreadyOnSavedZone(savedId)) {
    const { reconcileDiscoveredSavedZone } = await import('./zoneRestorePick');
    if (reconcileDiscoveredSavedZone(savedId)) {
      await syncDiscoveryActiveZone(
        useMaestroStore.getState().activeConnectorRenderer?.id || savedId,
      );
    } else {
      await syncDiscoveryActiveZone(savedId);
    }
    logZoneRestore('restored', { zoneId: savedId, phase: 'already_active' });
    return;
  }

  const deadline = Date.now() + BOOT_ZONE_RESTORE_WAIT_MS;
  const { getClientDiscoveryHandler } = await import('../handlers/ClientDiscoveryHandler');
  const handler = getClientDiscoveryHandler();

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, ZONE_POLL_MS));
    if (!canRestoreSavedZone()) {
      logZoneRestore('skipped_session_active', {});
      return;
    }
    await handler.refreshConnectorRenderers().catch(() => {});
    zones = useMaestroStore.getState().discovery?.discoveredZones || [];
    savedZone = findPreferredZone(savedId, zones);
    if (savedZone) {
      await handler.setActiveZone(savedZone.id).catch(() => {});
      applyZoneSelectionToStore(savedZone);
      await applySavedZoneVolume(savedZone.id);
      logZoneRestore('restored', {
        zoneId: savedZone.id,
        zoneName: savedZone.name,
        phase: 'after_wait',
        waitedMs: BOOT_ZONE_RESTORE_WAIT_MS - (deadline - Date.now()),
      });
      return;
    }
  }

  const active = useMaestroStore.getState().discovery?.activeZone;
  if (active && isSystemAudioZone(active) && !userExplicitlyChoseSystemAudio()) {
    applyPinnedSavedZone(savedId);
    await syncDiscoveryActiveZone(savedId);
    logZoneRestore('timeout_pinned', { savedId, zoneCount: zones.length });
    return;
  }
  logZoneRestore('fallback_system_audio', { savedId, zoneCount: zones.length });
}

export { runConnectorBootMount } from './rendererSessionReattach';
